import React, { useEffect, useRef } from 'react';
import { Box, Container, Typography, Button, Link as MuiLink, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { useOpenContactDialog } from '../contexts/ContactDialogContext';
import {
    INSTAGRAM_PLATFORM_BADGE,
    PRESS_LINKS,
    PRESS_LOGOS,
    PRESS_MENTIONS,
    PRESS_QUOTES,
    type PressQuote,
} from './landingConstants';
import { PRODUCT_LANDING_CARDS, LANDING_FOOTER_CTA } from './productPages';
import { MAIN_BG_COLOR } from '../theme';
import MarketingFooterCta from '../components/MarketingFooterCta';

const LANDING = {
    fontHeading: '"DM Serif Text", serif',
    fontBody: '"Inter", sans-serif',
    dark: MAIN_BG_COLOR,
    darkAlt: MAIN_BG_COLOR,
    cardDark: MAIN_BG_COLOR,
    offWhite: '#ffffff',
    mutedWhite: 'rgba(255,255,255,0.55)',
    mutedBlack: 'rgba(0,0,0,0.5)',
    borderLight: '0.5px solid rgba(255,255,255,0.12)',
    borderDark: '0.5px solid rgba(0,0,0,0.08)',
    sectionPaddingX: '1.25rem',
    breakpoint: 720,
} as const;

const PRESS_QUOTE_CAROUSEL_GAP_PX = 24;

const pressQuoteCardSx = {
    flex: '0 0 auto',
    width: { xs: 280, sm: 320, md: 340 },
    background: LANDING.darkAlt,
    border: LANDING.borderLight,
    borderRadius: '14px',
    p: 3,
    display: 'flex',
    flexDirection: 'column',
} as const;

const pressQuoteBlockquoteSx = {
    fontFamily: LANDING.fontHeading,
    fontStyle: 'italic',
    fontWeight: 400,
    fontSize: '1.125rem',
    lineHeight: 1.5,
    color: 'rgba(255,255,255,0.9)',
    mb: 2,
    flex: 1,
    display: '-webkit-box',
    WebkitLineClamp: 5,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
} as const;

const podscopeBrandMarkSx = {
    fontFamily: LANDING.fontHeading,
    fontStyle: 'normal',
    fontWeight: 800,
    color: '#fff',
} as const;

function highlightPodscopeBrand(text: string): React.ReactNode {
    const parts = text.split(/(@podscope\.co|Podscope)/gi);
    return parts.map((part, index) => {
        if (/^(@podscope\.co|Podscope)$/i.test(part)) {
            return (
                <Box key={index} component="span" sx={podscopeBrandMarkSx}>
                    {part}
                </Box>
            );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
}

/** Ranking-asset style thumb: SVG cover + badge with cutout gap (no CSS border). */
function PressThumbWithBadge({
    coverUrl,
    badgeUrl,
    sizePx = 48,
    alt = '',
}: {
    coverUrl: string;
    badgeUrl?: string;
    sizePx?: number;
    alt?: string;
}) {
    const reactId = React.useId().replace(/:/g, '');
    const maskId = `press-thumb-cutout-${reactId}`;

    // Match ranking-asset proportions (design space = 100px thumb).
    const designThumb = 100;
    const designBadge = 34;
    const designOverflow = 8;
    const designGap = 3;
    const designBadgeRx = 12;
    const designThumbRx = 20;

    const badge = {
        x: -designOverflow,
        y: designThumb - designBadge + designOverflow,
        size: designBadge,
        rx: designBadgeRx,
    };
    const cutout = {
        x: badge.x - designGap,
        y: badge.y - designGap,
        size: badge.size + designGap * 2,
        rx: badge.rx + designGap,
    };

    if (!badgeUrl) {
        return (
            <Box
                sx={{
                    width: sizePx,
                    height: sizePx,
                    borderRadius: `${Math.round(sizePx * 0.2)}px`,
                    overflow: 'hidden',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    flexShrink: 0,
                }}
            >
                <Box
                    component="img"
                    src={coverUrl}
                    alt={alt}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            </Box>
        );
    }

    const xhtmlImg = (src: string, w: number, h: number, borderRadius = 0) =>
        React.createElement(
            'div',
            {
                xmlns: 'http://www.w3.org/1999/xhtml',
                style: {
                    width: w,
                    height: h,
                    margin: 0,
                    padding: 0,
                    overflow: 'hidden',
                    borderRadius,
                    backgroundColor: 'rgba(33,35,45,0.85)',
                },
            },
            React.createElement('img', {
                src,
                alt: '',
                style: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                },
            })
        );

    return (
        <Box
            sx={{
                position: 'relative',
                width: sizePx,
                height: sizePx,
                flexShrink: 0,
                overflow: 'visible',
            }}
        >
            <Box
                component="svg"
                width={designThumb}
                height={designThumb}
                viewBox={`0 0 ${designThumb} ${designThumb}`}
                overflow="visible"
                sx={{
                    display: 'block',
                    width: sizePx,
                    height: sizePx,
                    overflow: 'visible',
                }}
            >
                <defs>
                    <clipPath id={`${maskId}-clip`} clipPathUnits="userSpaceOnUse">
                        <rect
                            width={designThumb}
                            height={designThumb}
                            rx={designThumbRx}
                            ry={designThumbRx}
                        />
                    </clipPath>
                    <mask id={maskId} maskUnits="userSpaceOnUse">
                        <rect width={designThumb} height={designThumb} fill="white" />
                        <rect
                            x={cutout.x}
                            y={cutout.y}
                            width={cutout.size}
                            height={cutout.size}
                            rx={cutout.rx}
                            ry={cutout.rx}
                            fill="black"
                        />
                    </mask>
                </defs>
                <g clipPath={`url(#${maskId}-clip)`}>
                    <foreignObject
                        width={designThumb}
                        height={designThumb}
                        mask={`url(#${maskId})`}
                    >
                        {xhtmlImg(coverUrl, designThumb, designThumb)}
                    </foreignObject>
                </g>
                <foreignObject x={badge.x} y={badge.y} width={badge.size} height={badge.size}>
                    {xhtmlImg(badgeUrl, badge.size, badge.size, badge.rx)}
                </foreignObject>
            </Box>
        </Box>
    );
}

const heroButtonMinHeight = 44;
const PRESS_MENTION_ICON_PX = 40;

const pressMentionPillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: PRESS_MENTION_ICON_PX + 20,
    height: PRESS_MENTION_ICON_PX + 20,
    boxSizing: 'border-box' as const,
    borderRadius: '12px',
    border: LANDING.borderLight,
    background: 'rgba(255,255,255,0.04)',
    textDecoration: 'none',
    overflow: 'visible',
    transition: 'background 0.2s ease, border-color 0.2s ease',
    '&:hover': {
        background: 'rgba(255,255,255,0.08)',
        borderColor: 'rgba(255,255,255,0.28)',
    },
} as const;

const pressMentionLogoSquareSx = {
    width: PRESS_MENTION_ICON_PX,
    height: PRESS_MENTION_ICON_PX,
    borderRadius: '8px',
    overflow: 'hidden',
    bgcolor: 'rgba(255,255,255,0.04)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& img': {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
    },
} as const;

const pillButtonWhite = {
    background: '#fff',
    color: MAIN_BG_COLOR,
    borderRadius: '100px',
    px: 2,
    py: 0.75,
    height: heroButtonMinHeight,
    minHeight: heroButtonMinHeight,
    boxSizing: 'border-box' as const,
    border: '1px solid transparent',
    fontFamily: LANDING.fontBody,
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1,
    textTransform: 'none' as const,
    '&:hover': { background: 'rgba(255,255,255,0.9)' },
};

const LandingPage: React.FC = () => {
    const openContactDialog = useOpenContactDialog();
    const quotesScrollRef = useRef<HTMLDivElement>(null);
    const quotesPausedRef = useRef(false);
    const quotesResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        analytics.track(AnalyticsEvent.VIEW_LANDING_PAGE);
    }, []);

    useEffect(() => {
        const el = quotesScrollRef.current;
        if (!el) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) return;

        let raf = 0;
        let last = performance.now();
        // ~half-loop in 90s (slower than the old 55s CSS marquee).
        const speedPxPerSec = () => Math.max(14, el.scrollWidth / 2 / 90);

        const tick = (now: number) => {
            const dt = Math.min(0.05, (now - last) / 1000);
            last = now;
            if (!quotesPausedRef.current) {
                el.scrollLeft += speedPxPerSec() * dt;
                const half = el.scrollWidth / 2;
                if (half > 0 && el.scrollLeft >= half) {
                    el.scrollLeft -= half;
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(raf);
            if (quotesResumeTimerRef.current) clearTimeout(quotesResumeTimerRef.current);
        };
    }, []);

    const pauseQuotesAutoScroll = (ms = 0) => {
        quotesPausedRef.current = true;
        if (quotesResumeTimerRef.current) clearTimeout(quotesResumeTimerRef.current);
        if (ms > 0) {
            quotesResumeTimerRef.current = setTimeout(() => {
                quotesPausedRef.current = false;
            }, ms);
        }
    };

    const scrollQuotesByCard = (direction: -1 | 1) => {
        const el = quotesScrollRef.current;
        if (!el) return;
        pauseQuotesAutoScroll(2500);
        const card = el.querySelector<HTMLElement>('[data-press-quote-card]');
        const amount = (card?.offsetWidth ?? 320) + PRESS_QUOTE_CAROUSEL_GAP_PX;
        const half = el.scrollWidth / 2;
        if (direction < 0 && half > 0 && el.scrollLeft < amount) {
            el.scrollLeft += half;
        }
        el.scrollBy({ left: direction * amount, behavior: 'smooth' });
    };

    const openDemo = () => {
        analytics.track(AnalyticsEvent.CLICK_REQUEST_DEMO, { location: 'hero' });
        openContactDialog();
    };

    const renderPressQuoteCard = (item: PressQuote, keyPrefix: string) => (
        <Box key={`${keyPrefix}-${item.id}`} data-press-quote-card="" sx={pressQuoteCardSx}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <PressThumbWithBadge
                    coverUrl={item.logo ?? PRESS_LOGOS[item.linkKey]}
                    badgeUrl={item.showInstagramBadge ? INSTAGRAM_PLATFORM_BADGE : undefined}
                    sizePx={48}
                    alt=""
                />
                <Typography
                    sx={{
                        fontFamily: LANDING.fontBody,
                        fontWeight: 400,
                        fontSize: '0.75rem',
                        color: 'rgba(255,255,255,0.5)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        lineHeight: 1.35,
                    }}
                >
                    {item.attribution}
                </Typography>
            </Box>
            {item.quotes.map((quote, quoteIndex) => (
                <Typography key={quoteIndex} component="blockquote" sx={pressQuoteBlockquoteSx}>
                    {quote.wrapQuotes === false ? (
                        <>&quot;{highlightPodscopeBrand(quote.text)}</>
                    ) : (
                        <>&quot;{highlightPodscopeBrand(quote.text)}&quot;</>
                    )}
                </Typography>
            ))}
            <MuiLink
                href={item.href ?? PRESS_LINKS[item.linkKey]}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={keyPrefix === 'dup' ? -1 : undefined}
                sx={{
                    fontFamily: LANDING.fontBody,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: 'rgba(255,255,255,0.8)',
                    textDecoration: 'none',
                    borderBottom: '0.5px solid rgba(255,255,255,0.4)',
                    alignSelf: 'flex-start',
                    mt: 'auto',
                    '&:hover': {
                        color: '#fff',
                        borderColor: 'rgba(255,255,255,0.7)',
                    },
                }}
            >
                {item.ctaLabel ?? 'Ver noticia'}
            </MuiLink>
        </Box>
    );

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                bgcolor: LANDING.dark,
            }}
        >
            <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* 1. Hero */}
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: '70vh',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: LANDING.dark,
                        px: { xs: LANDING.sectionPaddingX, md: 4 },
                        py: 8,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: -80,
                            top: -80,
                            backgroundImage: 'url("/images/vertical-background.svg")',
                            backgroundSize: 'cover',
                            backgroundPosition: 'bottom center',
                            backgroundRepeat: 'no-repeat',
                            opacity: 0.5,
                            pointerEvents: 'none',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            maxWidth: 960,
                            mx: 'auto',
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            component="h1"
                            sx={{
                                fontFamily: LANDING.fontHeading,
                                fontWeight: 800,
                                fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                                lineHeight: 1.15,
                                color: '#fff',
                                mb: 2,
                            }}
                        >
                            Descubre lo que está pasando en medios digitales
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: LANDING.fontBody,
                                fontWeight: 400,
                                fontSize: { xs: '1rem', md: '1.125rem' },
                                lineHeight: 1.6,
                                color: 'rgba(255,255,255,0.85)',
                                mb: 4,
                                maxWidth: 720,
                                mx: 'auto',
                            }}
                        >
                            Medimos y verificamos programas, streaming en vivo y clips en YouTube,
                            Instagram y TikTok, para darte datos verificados sobre tu marca, tu
                            competencia, tu categoría y tu propio contenido.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 2,
                            }}
                        >
                            <Button
                                component={Link}
                                to="/live"
                                startIcon={
                                    <Box
                                        sx={{
                                            width: '9px',
                                            height: '9px',
                                            borderRadius: '50%',
                                            backgroundColor: '#ef5350',
                                            flexShrink: 0,
                                            '@keyframes livePulse': {
                                                '0%': { opacity: 1, transform: 'scale(1)' },
                                                '50%': { opacity: 0.4, transform: 'scale(0.7)' },
                                                '100%': { opacity: 1, transform: 'scale(1)' },
                                            },
                                            animation: 'livePulse 1.6s ease-in-out infinite',
                                        }}
                                    />
                                }
                                sx={{
                                    borderRadius: '100px',
                                    px: 2,
                                    py: 0.75,
                                    height: heroButtonMinHeight,
                                    minHeight: heroButtonMinHeight,
                                    boxSizing: 'border-box',
                                    fontFamily: LANDING.fontBody,
                                    fontWeight: 500,
                                    fontSize: '1rem',
                                    lineHeight: 1,
                                    textTransform: 'none',
                                    color: '#ef5350',
                                    border: '1px solid rgba(239,83,80,0.4)',
                                    background: 'rgba(239,83,80,0.08)',
                                    '& .MuiButton-startIcon': {
                                        marginLeft: 0,
                                        marginRight: 1,
                                    },
                                    '&:hover': {
                                        background: 'rgba(239,83,80,0.15)',
                                        borderColor: '#ef5350',
                                    },
                                }}
                            >
                                Livestreams En Vivo
                            </Button>
                            <Button onClick={openDemo} sx={pillButtonWhite}>
                                Agenda una demo
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {/* 2. Press strip */}
                <Box
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        overflow: 'visible',
                        background: LANDING.dark,
                        borderTop: LANDING.borderLight,
                        borderBottom: LANDING.borderLight,
                        py: 2.5,
                        px: { xs: LANDING.sectionPaddingX, md: 4 },
                    }}
                >
                    <Container maxWidth="lg" sx={{ overflow: 'visible' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: { xs: 1.5, sm: 2 },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: LANDING.fontBody,
                                    fontWeight: 400,
                                    fontSize: '0.75rem',
                                    color: 'rgba(255,255,255,0.5)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                Mencionado en
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                }}
                            >
                                {PRESS_MENTIONS.map(item => {
                                    const hasBadge = 'badgeLogo' in item && Boolean(item.badgeLogo);
                                    return (
                                        <MuiLink
                                            key={item.label}
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={item.label}
                                            sx={pressMentionPillSx}
                                        >
                                            {hasBadge ? (
                                                <PressThumbWithBadge
                                                    coverUrl={item.logo}
                                                    badgeUrl={
                                                        'badgeLogo' in item
                                                            ? item.badgeLogo
                                                            : undefined
                                                    }
                                                    sizePx={PRESS_MENTION_ICON_PX}
                                                    alt={item.label}
                                                />
                                            ) : (
                                                <Box sx={pressMentionLogoSquareSx}>
                                                    <Box
                                                        component="img"
                                                        src={item.logo}
                                                        alt={item.label}
                                                    />
                                                </Box>
                                            )}
                                        </MuiLink>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Container>
                </Box>

                {/* 3. News quotes — auto-scroll marquee */}
                <Box
                    sx={{
                        width: '100%',
                        background: LANDING.dark,
                        py: { xs: 6, md: 8 },
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        ref={quotesScrollRef}
                        onMouseEnter={() => pauseQuotesAutoScroll()}
                        onMouseLeave={() => {
                            quotesPausedRef.current = false;
                        }}
                        sx={{
                            width: '100%',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            maskImage:
                                'linear-gradient(to right, transparent, #000 4%, #000 96%, transparent)',
                            WebkitMaskImage:
                                'linear-gradient(to right, transparent, #000 4%, #000 96%, transparent)',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                width: 'max-content',
                            }}
                        >
                            {[0, 1].map(copy => (
                                <Box
                                    key={copy}
                                    sx={{
                                        display: 'flex',
                                        gap: `${PRESS_QUOTE_CAROUSEL_GAP_PX}px`,
                                        flexShrink: 0,
                                        pr: `${PRESS_QUOTE_CAROUSEL_GAP_PX}px`,
                                    }}
                                    aria-hidden={copy === 1 || undefined}
                                >
                                    {PRESS_QUOTES.map(item =>
                                        renderPressQuoteCard(item, copy === 0 ? 'a' : 'dup')
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 0.5,
                            mt: 2.5,
                        }}
                    >
                        <IconButton
                            aria-label="Citas anteriores"
                            onClick={() => scrollQuotesByCard(-1)}
                            size="small"
                            sx={{
                                color: 'rgba(255,255,255,0.45)',
                                p: 0.5,
                                '&:hover': {
                                    color: 'rgba(255,255,255,0.85)',
                                    background: 'rgba(255,255,255,0.06)',
                                },
                            }}
                        >
                            <ChevronLeftIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                        <IconButton
                            aria-label="Citas siguientes"
                            onClick={() => scrollQuotesByCard(1)}
                            size="small"
                            sx={{
                                color: 'rgba(255,255,255,0.45)',
                                p: 0.5,
                                '&:hover': {
                                    color: 'rgba(255,255,255,0.85)',
                                    background: 'rgba(255,255,255,0.06)',
                                },
                            }}
                        >
                            <ChevronRightIcon sx={{ fontSize: 22 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* 4. Products section */}
                <Box
                    id="productos"
                    sx={{
                        width: '100%',
                        background: LANDING.offWhite,
                        borderTop: LANDING.borderDark,
                        py: { xs: 6, md: 8 },
                        px: { xs: LANDING.sectionPaddingX, md: 4 },
                    }}
                >
                    <Container maxWidth="lg">
                        <Typography
                            component="h2"
                            sx={{
                                fontFamily: LANDING.fontHeading,
                                fontWeight: 700,
                                fontSize: { xs: '1.75rem', md: '2.25rem' },
                                lineHeight: 1.25,
                                color: MAIN_BG_COLOR,
                                textAlign: 'center',
                                maxWidth: 720,
                                mx: 'auto',
                                mb: { xs: 4, md: 5 },
                            }}
                        >
                            Hacemos medible todo lo que se dice y aparece en medios digitales
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                    md: '1fr 1fr 1fr',
                                },
                                gap: 2,
                            }}
                        >
                            {PRODUCT_LANDING_CARDS.map(
                                ({ id, title, description, Icon, pagePath }) => (
                                    <Box
                                        key={id}
                                        component={Link}
                                        to={`${pagePath}#${id}`}
                                        id={id}
                                        sx={{
                                            background: '#f4f4f5',
                                            borderRadius: '12px',
                                            p: { xs: 2.5, md: 3 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            textAlign: 'left',
                                            scrollMarginTop: 88,
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            transition: 'background 0.15s ease',
                                            '&:hover': {
                                                background: '#ececee',
                                            },
                                        }}
                                    >
                                        <Icon
                                            sx={{
                                                color: MAIN_BG_COLOR,
                                                fontSize: 28,
                                                mb: 2,
                                            }}
                                        />
                                        <Typography
                                            component="h3"
                                            sx={{
                                                fontFamily: LANDING.fontBody,
                                                fontWeight: 700,
                                                fontSize: '1.0625rem',
                                                lineHeight: 1.3,
                                                color: MAIN_BG_COLOR,
                                                mb: 1,
                                            }}
                                        >
                                            {title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: LANDING.fontBody,
                                                fontWeight: 400,
                                                fontSize: '0.9375rem',
                                                lineHeight: 1.55,
                                                color: 'rgba(0,0,0,0.55)',
                                            }}
                                        >
                                            {description}
                                        </Typography>
                                    </Box>
                                )
                            )}
                        </Box>
                    </Container>
                </Box>

                <MarketingFooterCta
                    title={LANDING_FOOTER_CTA.title}
                    ctaLabel={LANDING_FOOTER_CTA.ctaLabel}
                    ctaTo={LANDING_FOOTER_CTA.ctaTo}
                    analyticsLocation="landing_footer"
                />
            </Box>
        </Box>
    );
};

export default LandingPage;
